import { useMemo } from 'react';

import type { ITimelineProps, TStepStatus } from '../../types/timeline';
import { STEP_ICON_MAPPER, STEPS_STATUS } from './utils';
import { SkeletonBase } from '../SkeletonLoader/SkeletonBase';

import styles from './timeline.module.scss';

export const Timeline = ({
  width = '100%',
  steps: stepsArr = [],
  title,
  footer,
  defaultIconProps = {},
  skeletonCount = 3,
  isLoading,
}: ITimelineProps) => {
  const steps = useMemo(() => {
    return isLoading
      ? Array.from({ length: skeletonCount }, (_, idx) => ({
          step: '',
          description: '',
          date: '',
          status: STEPS_STATUS.DEFAULT,
          id: `loading-step-${idx}`,
          children: null,
          icon: null,
        }))
      : stepsArr;
  }, [isLoading, stepsArr, skeletonCount]);

  if (!isLoading && !stepsArr.length) return null;

  return (
    <div className={styles['timeline']} style={{ width }}>
      {title && <h2 className={styles['timeline-title']}>{title}</h2>}

      {steps.map((step, idx) => {
        const isLastItem = idx === steps.length - 1;
        const currentStatus = step.status;
        const nextStatus = steps[idx + 1]?.status;
        const isDefaultStep = [nextStatus, currentStatus].includes(
          STEPS_STATUS.DEFAULT
        );
        const icon =
          step.icon ||
          STEP_ICON_MAPPER(defaultIconProps)[step.status as TStepStatus];

        return (
          <div
            className={styles['timeline__item']}
            key={step.id || `timeline-${step.step}-${idx}`}
            data-timeline-item-status={step.status}
          >
            <div
              className={styles['timeline__item-status']}
              data-timeline-use-default-color={!!defaultIconProps.color}
            >
              <div
                className={styles['timeline__item-status-icon']}
                data-timeline-loading={isLoading}
              >
                {isLoading ? <SkeletonBase height="2rem" width="2rem" /> : icon}
              </div>

              {!isLastItem && (
                <div
                  className={styles['timeline__item-status-line']}
                  data-timeline-current-status-line={
                    isDefaultStep || isLoading
                      ? STEPS_STATUS.DEFAULT
                      : currentStatus
                  }
                  data-timeline-next-status-line={
                    isDefaultStep || isLoading
                      ? STEPS_STATUS.DEFAULT
                      : nextStatus
                  }
                ></div>
              )}
            </div>
            <div className={styles['timeline__item-body']}>
              <div className={styles['timeline__item-body-content']}>
                <div className={styles['timeline__item-body-content-main']}>
                  {isLoading ? (
                    <>
                      <SkeletonBase height="1.5rem" width="8rem" />
                      <SkeletonBase height="2rem" width="12rem" />
                    </>
                  ) : (
                    <>
                      <p
                        className={
                          styles['timeline__item-body-content-main-title']
                        }
                      >
                        {step.step}
                      </p>
                      <p
                        className={
                          styles['timeline__item-body-content-main-desc']
                        }
                      >
                        {step.description}
                      </p>
                    </>
                  )}
                </div>
                {isLoading ? (
                  <SkeletonBase height="1rem" width="10rem" />
                ) : (
                  step.date && (
                    <p className={styles['timeline__item-body-content-date']}>
                      {step.date}
                    </p>
                  )
                )}
              </div>
              {step.children && (
                <div className={styles['timeline__item-body-children']}>
                  {step.children}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {footer}
    </div>
  );
};
